import axios, { AxiosResponse } from "axios";
import { WebAPI, IFailCallbackParam } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";
import { MacaddrMemo } from "./common";

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIでMACアドレスの登録が成功した場合に呼び出されます
   * @callback success
   * @param {number} status - httpステータス
   */
  success: (status: number) => void;
}

export interface IRegistMacaddrRequestModel {
  /**
   * MACアドレス
   */
  macaddr: string;
  /**
   * メモ
   */
  memo: string;
}

function AxiosPost(
  url: string,
  params: IRegistMacaddrRequestModel,
  token?: string
) {
  if (token) {
    return axios.post(url, JSON.stringify(params), {
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    });
  } else {
    return axios.post(url, JSON.stringify(params), {
      headers: { "Content-Type": "application/json" },
    });
  }
}

interface IAxiosInternalParams extends ICallbackParams {
  promise: Promise<AxiosResponse>;
}

function AxiosInternal(params: IAxiosInternalParams) {
  params.promise
    .then((res) => {
      params.success(res.status);
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    });
}

export interface IRegistMacaddrParams
  extends IRegistMacaddrRequestModel,
    ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
}

/**
 * MACアドレスを登録します
 * @function registMacaddr
 * @param {IRegistMacaddrRequestModel} params - MACアドレス登録パラメータ
 */
export function registMacaddr(params: IRegistMacaddrParams): void {
  const { authService, macaddr, memo } = params;
  const req: IRegistMacaddrRequestModel = {
    macaddr: macaddr,
    memo: MacaddrMemo(memo),
  };
  const url = WebAPI.REGIST_MACADDR;
  if (authService) {
    authService.getToken({
      success: (token) => {
        AxiosInternal({
          promise: AxiosPost(url, req, token),
          success: params.success,
          fail: (id, status) => {
            if (status === 401) {
              registMacaddrRetry401(authService, url, req, params);
            } else {
              params.fail(id, status);
            }
          },
        });
      },
      fail: (err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      },
    });
  } else {
    AxiosInternal({
      promise: AxiosPost(url, req),
      success: params.success,
      fail: params.fail,
    });
  }
}

/**
 * MACアドレスを登録します
 * @function registMacaddrRetry401
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - リクエストURL
 * @param {IRegistMacaddrRequestModel} req - リクエストパラメータ
 * @param {ICallbackParams} params - コールバックパラメータ
 */
function registMacaddrRetry401(
  authService: AuthService,
  url: string,
  req: IRegistMacaddrRequestModel,
  params: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      AxiosInternal({
        promise: AxiosPost(url, req, token),
        success: params.success,
        fail: params.fail,
      });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    },
  });
}
